/**
 * MinistrareIA - Public Video Player Page
 * Displays an HLS video with view-limit security.
 * Routes: /v/:videoId and /v/:videoId/:leadId
 * No authentication required.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Hls from 'hls.js';
import videoLinkService from '../../services/global/videoLinkService';

// Generate or retrieve a persistent device ID
const getDeviceId = () => {
  let id = localStorage.getItem('lumen_device_id');
  if (!id) {
    id = 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('lumen_device_id', id);
  }
  return id;
};

const VideoPlayerPage = () => {
  const { videoId, leadId } = useParams();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [viewInfo, setViewInfo] = useState(null);
  const [hasRecordedView, setHasRecordedView] = useState(false);

  // Determine viewer identifier
  const viewerIdentifier = leadId || getDeviceId();
  const viewerType = leadId ? 'lead' : 'device';

  // Load video data
  useEffect(() => {
    const loadVideo = async () => {
      try {
        setLoading(true);
        const res = await videoLinkService.getPublicVideo(videoId, leadId);
        const data = res.data;

        if (!data) {
          setError('Video nao encontrado');
          return;
        }

        // Check if already blocked (for lead-based links)
        if (data.viewer_status && !data.viewer_status.allowed) {
          setBlocked(true);
          setViewInfo(data.viewer_status);
          return;
        }

        setVideoData(data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Video nao encontrado ou ainda sendo processado');
        } else {
          setError('Erro ao carregar video');
        }
      } finally {
        setLoading(false);
      }
    };

    loadVideo();
  }, [videoId, leadId]);

  // Initialize HLS player
  useEffect(() => {
    if (!videoData?.hls_playlist_url || !videoRef.current) return;

    const video = videoRef.current;
    const hlsUrl = videoData.hls_playlist_url;

    if (Hls.isSupported()) {
      const hls = new Hls({
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error('HLS fatal error:', data);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          }
        }
      });
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = hlsUrl;
    } else {
      setError('Seu navegador nao suporta reproducao de video HLS');
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [videoData]);

  // Record view on play
  const handlePlay = useCallback(async () => {
    if (hasRecordedView || blocked) return;

    try {
      const res = await videoLinkService.recordView(
        videoId, viewerIdentifier, viewerType, leadId || null
      );
      const data = res.data;
      setViewInfo(data);
      setHasRecordedView(true);

      if (!data.allowed) {
        setBlocked(true);
        if (videoRef.current) {
          videoRef.current.pause();
        }
      }
    } catch (err) {
      console.error('Error recording view:', err);
    }
  }, [videoId, viewerIdentifier, viewerType, leadId, hasRecordedView, blocked]);

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loader}>
          <div style={styles.spinner} />
          <span style={{ color: '#aaa', marginTop: '12px' }}>Carregando video...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.messageCard}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9888;</div>
          <h2 style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '20px' }}>{error}</h2>
          <p style={{ color: '#aaa', margin: 0, fontSize: '14px' }}>
            Verifique se o link esta correto e tente novamente.
          </p>
        </div>
      </div>
    );
  }

  // Blocked state
  if (blocked) {
    return (
      <div style={styles.container}>
        <div style={styles.messageCard}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#128274;</div>
          <h2 style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '20px' }}>
            Limite de visualizacoes atingido
          </h2>
          <p style={{ color: '#aaa', margin: 0, fontSize: '14px' }}>
            Voce ja assistiu este video {viewInfo?.current_views || '?'} de {viewInfo?.limit || '?'} vezes permitidas.
          </p>
        </div>
      </div>
    );
  }

  // Player
  return (
    <div style={styles.container}>
      <div style={styles.playerWrapper}>
        {videoData?.name && (
          <h1 style={{ color: '#fff', fontSize: '18px', fontWeight: 600, margin: '0 0 16px 0', textAlign: 'center' }}>
            {videoData.name}
          </h1>
        )}

        <div style={styles.videoContainer}>
          <video
            ref={videoRef}
            controls
            playsInline
            onPlay={handlePlay}
            poster={videoData?.thumbnail_url || undefined}
            style={styles.video}
          />

          {/* Blocked overlay (shows if blocked during playback) */}
          {blocked && (
            <div style={styles.blockedOverlay}>
              <div style={{ fontSize: '48px' }}>&#128274;</div>
              <h3 style={{ color: '#fff', margin: '8px 0' }}>Limite atingido</h3>
              <p style={{ color: '#ccc', fontSize: '14px', margin: 0 }}>
                {viewInfo?.current_views}/{viewInfo?.limit} visualizacoes
              </p>
            </div>
          )}
        </div>

        {viewInfo && !blocked && (
          <div style={styles.viewCounter}>
            Visualizacao {viewInfo.current_views} de {viewInfo.limit}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #333',
    borderTopColor: '#00a884',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  messageCard: {
    textAlign: 'center',
    padding: '40px',
    maxWidth: '400px',
  },
  playerWrapper: {
    width: '100%',
    maxWidth: '900px',
  },
  videoContainer: {
    position: 'relative',
    width: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: '#000',
    aspectRatio: '16/9',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  blockedOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewCounter: {
    textAlign: 'center',
    marginTop: '12px',
    padding: '8px 16px',
    borderRadius: '20px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#aaa',
    fontSize: '13px',
    display: 'inline-block',
    width: 'fit-content',
    margin: '12px auto 0',
  },
};

export default VideoPlayerPage;
